import React from 'react';
import { cn } from '@/lib/utils';
import { SubNavSection } from './SubNavSection';
import { SubNavActivities } from './SubNavActivities';
import { SubNavActivityDetails } from './SubNavActivityDetails';
import type { SubNavGroup } from './types';
import type { Activity, ActivityDetailsData } from '@/components/custom/ActivityMenu';

export interface SubNavCardProps extends React.HTMLAttributes<HTMLDivElement> {
  navigationData: SubNavGroup[];
  activeItem?: string;
  activeTab?: string;
  onItemClick?: (groupTitle: string, itemHref: string) => void;
  showActivities?: boolean;
  recentActivities?: Activity[];
  activityDetails?: ActivityDetailsData;
  onViewAllActivities?: () => void;
  onOpenRecord?: () => void;
  // When provided, enables ESC-to-close behavior and shows an "Exit ESC" affordance
  onClose?: () => void;
  /** Custom link component for routing (e.g., React Router Link) */
  linkComponent?: React.ElementType;
}

export const SubNavCard = React.forwardRef<HTMLDivElement, SubNavCardProps>(
  ({ 
    navigationData, 
    activeItem, 
    activeTab, 
    onItemClick,
    showActivities = false,
    recentActivities = [],
    activityDetails,
    onViewAllActivities,
    onOpenRecord,
    onClose,
    linkComponent,
    className,
    ...props 
  }, ref) => {
    
    if (!navigationData || navigationData.length === 0) {
      return null;
    }

    // activeTab is used to style the active group tiles; no need to compute activeGroup here


    return (
      <div 
        ref={ref}
        className={cn(
          "w-full bg-sky-50 rounded-xl p-2 shadow-lg",
          className
        )}
        {...props}
      >
        <div className="bg-sky-100 rounded-lg p-3">
          {/* Navigation Grid */}
          <div className={cn(
            "grid gap-1",
            "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
          )}>
            {navigationData.map((group) => {
              const isActive = group.title?.toLowerCase() === activeTab?.toLowerCase();
              return (
                <div
                  key={group.title}
                  className={cn(
                    "rounded-lg p-2 transition-all",
                    isActive ? "bg-white ring-3 ring-sky-200" : "bg-gray-50"
                  )}
                >
                  <SubNavSection
                    title={group.title}
                    items={group.items}
                    activeItem={activeItem}
                    onItemClick={onItemClick}
                    variant="card"
                    linkComponent={linkComponent}
                  />
                </div>
              );
            })}
          </div>

          {/* Activities Section */}
          {showActivities && (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {recentActivities.length > 0 && (
                <SubNavActivities
                  activities={recentActivities}
                  onViewAll={onViewAllActivities}
                  variant="card"
                />
              )}
              {activityDetails && (
                <SubNavActivityDetails
                  details={activityDetails}
                  onOpenRecord={onOpenRecord}
                  variant="card"
                />
              )}
            </div>
          )}

          {/* Exit hint (ESC) */}
          {onClose && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  "flex items-center gap-2 rounded-md",
                  "bg-white/80 text-gray-700 text-xs",
                  "px-3 py-1",
                  "ring-1 ring-gray-200 shadow-sm hover:bg-white"
                )}
                aria-label="Exit sub-navigation (ESC)"
              >
                <span>Exit</span>
                <kbd className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[10px] leading-none">
                  ESC
                </kbd>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

SubNavCard.displayName = 'SubNavCard';

